import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import {
  Wallet,
  ArrowRight,
  TrendingUp,
  PieChart,
  Lock,
  ShieldCheck,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  BarChart3,
  Clock,
  Layers,
  Eye,
  BookOpen,
  Coins,
} from "lucide-react";

const steps = [
  {
    number: "01",
    title: "Deposit USDT",
    description: "Transfer USDT to your PassivePay account securely.",
    icon: Wallet,
  },
  {
    number: "02",
    title: "Choose an Investment Category",
    description: "Select from equity, stocks, or fixed APY plans.",
    icon: PieChart,
  },
  {
    number: "03",
    title: "Earn Periodic Returns",
    description: "Watch your investment grow with managed strategies.",
    icon: TrendingUp,
  },
  {
    number: "04",
    title: "Withdraw as per Plan Terms",
    description: "Access your funds according to your plan's terms.",
    icon: DollarSign,
  },
];

const investmentOptions = [
  {
    title: "Equity-Based Investments",
    badge: "Market-linked growth",
    targetApy: "Variable",
    description: "Invest USDT into equity-linked strategies focused on long-term capital appreciation through global stocks and funds.",
    keyPoints: [
      "Market-linked returns",
      "Higher growth potential",
      "Medium to long-term horizon",
    ],
    icon: TrendingUp,
    color: "primary",
  },
  {
    title: "Stock & Fund Allocation",
    badge: "Lower volatility focus",
    targetApy: "Moderate",
    description: "Diversified exposure to selected stocks and funds aimed at balanced growth with controlled volatility.",
    keyPoints: [
      "Diversified allocation",
      "Lower risk than direct equity exposure",
      "Periodic rebalancing",
    ],
    icon: PieChart,
    color: "secondary",
  },
  {
    title: "Fixed APY Plans",
    badge: "Best for steady income",
    targetApy: "Fixed Rate",
    subBadge: "Defined Duration",
    description: "Earn a fixed-rate return on your USDT over a predefined period through structured yield strategies.",
    keyPoints: [
      "Fixed APY for the plan duration",
      "Short to medium lock-in periods",
      "Designed for predictable income",
    ],
    icon: Lock,
    color: "accent",
  },
  {
    title: "XAU Trading Pool",
    badge: "Gold Trading",
    targetApy: "Performance-Based",
    subBadge: "XAU/USD",
    description: "A collective capital pool focused exclusively on Gold (XAU/USD) trading. Profits distributed based on your percentage ownership of total pool capital.",
    keyPoints: [
      "Structured capital participation model",
      "Unified trading strategy",
      "13% performance fee (only on profits)",
      "Fee paid in PayCoin",
    ],
    icon: Coins,
    color: "yellow",
    isGold: true,
  },
];

const whyInvest = [
  {
    icon: DollarSign,
    title: "USDT-Based Investing",
    description: "Reduced volatility compared to crypto pairs",
  },
  {
    icon: Clock,
    title: "Clear Plan Durations",
    description: "Defined terms & yield expectations",
  },
  {
    icon: Eye,
    title: "No Hidden Fees",
    description: "Transparent fee structure",
  },
  {
    icon: BookOpen,
    title: "Education-First Approach",
    description: "Learn while you earn",
  },
  {
    icon: ShieldCheck,
    title: "Transparent Risk Disclosures",
    description: "Clear communication on all risks",
  },
];

export default function Earn() {
  return (
    <div className="min-h-screen bg-background">
      <NavBar />

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[150px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Wallet className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Passive Income</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black mb-6">
              Put Your Stablecoins to <span className="text-primary">Work</span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground mb-8">
              Invest USDT into curated equities, stocks, and yield-focused strategies designed for steady growth.
            </p>

            <Link to="/register">
              <Button size="lg" className="neon-glow font-heading font-bold uppercase tracking-wider">
                Start Earning <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Section 1: How It Works */}
      <section className="py-20 relative bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              How It <span className="text-primary">Works</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Simple steps to start earning passive income with your USDT
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card rounded-xl p-6 relative"
                >
                  <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-primary flex items-center justify-center font-heading font-bold text-sm">
                    {step.number}
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mb-4 mt-2">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-muted-foreground mt-8 text-sm"
          >
            All investments are managed with risk-aware strategies.
          </motion.p>
        </div>
      </section>

      {/* Section 2: Investment Options */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              Investment <span className="text-secondary">Options</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Choose from diversified investment strategies based on your risk appetite
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {investmentOptions.map((option, i) => {
              const Icon = option.icon;
              const isGold = option.isGold;

              const badgeBg = isGold ? "bg-yellow-400/20" : `bg-${option.color}/20`;
              const badgeText = isGold ? "text-yellow-400" : `text-${option.color}`;
              const badgeBorder = isGold ? "border-yellow-400/30" : `border-${option.color}/30`;
              const iconBg = isGold ? "bg-yellow-400/20" : `bg-${option.color}/20`;
              const iconText = isGold ? "text-yellow-400" : `text-${option.color}`;
              const hoverBorder = isGold ? "hover:border-yellow-400/30" : `hover:border-${option.color}/30`;
              const checkColor = isGold ? "text-yellow-400" : `text-${option.color}`;

              return (
                <motion.div
                  key={option.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  data-testid={`investment-card-${i}`}
                  className={`glass-card rounded-2xl p-8 ${hoverBorder} transition-colors relative flex flex-col`}
                >
                  {/* Badge */}
                  <div className="absolute -top-3 left-6">
                    <span className={`px-3 py-1 text-xs font-medium ${badgeBg} ${badgeText} rounded-full border ${badgeBorder}`}>
                      {option.badge}
                    </span>
                  </div>

                  <div className={`w-14 h-14 rounded-xl ${iconBg} flex items-center justify-center mb-6 mt-2`}>
                    <Icon className={`w-7 h-7 ${iconText}`} />
                  </div>

                  <h3 className="text-xl font-heading font-bold mb-2">{option.title}</h3>
                  
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm text-muted-foreground">Target APY:</span>
                    <span className={`font-heading font-bold ${badgeText}`}>{option.targetApy}</span>
                    {option.subBadge && (
                      <span className="text-xs px-2 py-0.5 bg-white/10 rounded-full">{option.subBadge}</span>
                    )}
                  </div>

                  <p className="text-muted-foreground mb-6 flex-grow">{option.description}</p>

                  <div className="space-y-2 mb-6">
                    {option.keyPoints.map((point, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${checkColor} flex-shrink-0`} />
                        <span className="text-sm">{point}</span>
                      </div>
                    ))}
                  </div>

                  {option.disclaimer && (
                    <div className="mt-auto pt-4 border-t border-white/10">
                      <p className="text-xs text-muted-foreground flex items-start gap-2">
                        <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5 text-yellow-500" />
                        {option.disclaimer}
                      </p>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4: Why Invest with PassivePay */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              Why Invest with <span className="text-primary">PassivePay</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {whyInvest.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card rounded-xl p-6 text-center hover:border-primary/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold mb-2 text-sm">{item.title}</h3>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 5: Risk Disclosure */}
      <section className="py-12 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-xl p-6 border-red-500/30 bg-red-500/5"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-heading font-bold mb-3 text-red-400">Important Risk Disclosure</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">•</span>
                    Investing involves risk, including possible loss of capital.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">•</span>
                    Fixed APY plans indicate rate structure, not guaranteed profit.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">•</span>
                    Past performance does not indicate future results.
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative bg-card/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 md:p-12 border-primary/30 text-center"
          >
            <Layers className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-4">
              Start Earning with <span className="text-primary">USDT</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Choose a plan that fits your risk appetite.
            </p>
            <Link to="/contact">
              <Button size="lg" className="neon-glow font-heading font-bold uppercase tracking-wider">
                Explore Investment Options <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground mt-6">
              PassivePay retains 40% of earned profits as management fee.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
